import { FastlyImage } from '@components/common/image'
import type { ReactNode } from 'react'
import { defineMessage, Trans } from '@lingui/macro'
import MsgTrans from '@components/common/i18n/message-trans'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { LinguiJSMessageDescriptor } from '@lib/types/common'

export type StatisticsProps = {
  title: string
  description: LinguiJSMessageDescriptor
  backgroundColor?: string
}

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1000+',
    description: defineMessage({
      message: 'Graduate CS instructors',
    }),
    backgroundColor: 'bg-orange-200',
  },
  {
    title: '👨‍💻 7+',
    description: defineMessage({
      message: 'Years of coding experience',
    }),
    backgroundColor: 'bg-blue-50',
  },
  {
    title: '👩 91%',
    description: defineMessage({
      message: 'Female educators',
    }),
    backgroundColor: 'bg-yellow-100',
  },
  {
    title: '💻 10+',
    description: defineMessage({
      message: 'Programming languages',
    }),
    backgroundColor: 'bg-green-50',
  },
]

type Props = {
  title?: string | ReactNode
  urlQuery?: string
  ctaText?: string
  ctaLink?: string
  statistics?: StatisticsProps[]
  imagePath?: string
  description?: JSX.Element
}

const defaultTitle = (
  <>
    <Trans id="">
      Learn coding from the{' '}
      <span className="yellow-underline">experienced teachers.</span> Our
      hand-picked coding teachers ensure the best learning experience.
    </Trans>
  </>
)

const Teachers = ({
  title = defaultTitle,
  urlQuery,
  statistics,
  imagePath,
  description,
}: Props) => {
  const statisticsContent = statistics || STATISTICS
  return (
    <div className="container mx-auto my-10 text-grey lg:px-3 py-5">
      <h2 className="text-2xl font-600 leading-tight mb-8">{title}</h2>

      <div className="flex flex-col items-center lg:flex-row gap-10 lg:gap-6 xl:gap-10 justify-between leading-tight mb-12">
        <div className="hidden lg:block lg:w-1/2">
          <FastlyImage
            src={`${
              imagePath
                ? `${imagePath}.png`
                : '/images/locale-images/us/teachers/teachers-v3.png'
            }`}
            webpSrc={`${
              imagePath
                ? `${imagePath}.webp`
                : '/images/locale-images/us/teachers/teachers-v3.webp'
            }`}
            height={426}
            width={471}
            alt="Codingal teachers"
          />
        </div>

        <p className="lg:text-lg lg:w-2/3">{description}</p>

        <div className="lg:hidden">
          <FastlyImage
            src={`${
              imagePath
                ? `${imagePath}.png`
                : '/images/locale-images/us/teachers/teachers-v3.png'
            }`}
            webpSrc={`${
              imagePath
                ? `${imagePath}.webp`
                : '/images/locale-images/us/teachers/teachers-v3.webp'
            }`}
            height={500}
            width={500}
            alt="Codingal teachers"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 my-12 rounded-lg shadow-lg p-5">
        {statisticsContent.map((stat, i) => (
          <div
            key={i}
            className={`${
              stat.backgroundColor ?? 'text-orange-200'
            } shadow-inner p-3 text-xl flex-1 flex flex-col rounded-md`}
          >
            <h3 className="text-2xl font-700 pb-2">{stat.title}</h3>
            <p className="break-words">
              <MsgTrans msg={stat.description} />
            </p>
          </div>
        ))}
      </div>
      <div className="mx-auto w-full md:max-w-xs text-center">
        <BookFreeClassBtn query={urlQuery} />
      </div>
    </div>
  )
}

export default Teachers
